// src/stores/user.ts
import { defineStore } from "pinia";
import { getMyProfile, getPackages } from "@/api/user";
import { activateSuperBoom as activateSuperBoomAPI } from "@/api/recommendations";
import { getPhoto } from "../api/user";

export interface User {
    photos?: UserImageServer[];
    images: UserImage[];
    information: UserInformation;
}

export interface UserImage {
    id: number;
    image: string;
}

export interface UserImageServer {
    fid: string;
    id: number;
    url: string;
    is_main: boolean;
}

export interface UserInformation {
    id: string;
    name: string;
    age: number;
    email: string;
    phone: string | null;
    need_bind_phone?: boolean;
    bind_phone_message?: string | null;
    birth_date: string;
    birth_time?: string;
    registration_screen: string | null;
    registration_date: string;
    show_my_gender: boolean;
    username: string | null;
    show_me: string[];
    residence: string;
    bio: string;
    gender: KeyName | null;
    sexual_orientation: KeyName | null;
    zodiac_sign: KeyName | null;
    education: KeyName | null;
    family: KeyName | null;
    communication: KeyName | null;
    love_language: KeyName | null;
    alcohol: KeyName | null;
    smoking: KeyName | null;
    sport: KeyName | null;
    food: KeyName | null;
    social_network: KeyName | null;
    sleep: KeyName | null;
    educational_institution: string;
    family_status: KeyName | null;
    pets: KeyName[];
    interests: UserInterest[];
    relationship_preference: RelationshipPreference | null;
    role: string;
    company: string;
    compatibility: number;
    superlikes: number;
    superbooms: number;
    likes: string | number;
    show_distance_from_me: boolean;
    show_my_age: boolean;
    show_my_orientation: boolean;
    is_verified: boolean;
    packages: UserPackage[];
    clubs?: { id: number; club_id: number; user_id: string; source: string }[];
}

export interface KeyName {
    name: string;
    key: string;
}

export interface UserInterest {
    name: string;
    id: number;
}

export interface RelationshipPreference {
    preference: string;
    id: number;
}

export interface UserPackage {
    subscription_package: SubscriptionPackageInfo;
    superboom_due_date: string;
    superbooms: number;
    superlikes: number;
}

export interface SubscriptionPackageInfo {
    type: string;
    due_date: string;
}

export const useUserStore = defineStore("user", {
    state: () => ({
        user: null as User | null,
        mainImage: null as UserImageServer | null,
        loading: false,
        error: null as string | null,
    }),

    getters: {
        activeSubscriptionName(state): string | null {
            return state.user?.information?.packages?.[0]?.subscription_package?.type || null;
        },
        activeSubscriptionDate(state): string | null {
            return state.user?.information?.packages?.[0]?.subscription_package?.due_date || null;
        },
        timerSuperBoom(state): string | null {
            return state.user?.information?.packages?.[0]?.superboom_due_date || null;
        },

        subscriptionType(state): string | null {
            const raw = state.user?.information?.packages?.[0]?.subscription_package?.type;
            if (!raw) return null;

            // "Tinderone Premium" -> "premium", "Premium" -> "premium"
            return raw.replace(/^tinderone\s+/i, "").trim().toLowerCase();
        },

        hasActivePremiumSubscription(state): boolean {
            const packs = state.user?.information?.packages || [];
            const now = Date.now();

            return packs.some((pack: any) => {
                const pkg = pack?.subscription_package || pack;
                const type = String(pkg?.type || "").replace(/^tinderone\s+/i, "").trim().toLowerCase();

                if (!type.includes("premium")) return false;

                const dueDate = pkg?.due_date || pack?.due_date || pkg?.active_until || pack?.active_until;
                if (!dueDate) return true;

                const ts = Date.parse(String(dueDate).replace(" ", "T"));
                return Number.isFinite(ts) ? ts > now : true;
            });
        },

        hasActiveSubscription(state): boolean {
            const pkg = state.user?.information?.packages?.[0]?.subscription_package;
            if (!pkg?.type || !pkg?.due_date) return false;

            const due = new Date(pkg.due_date);
            return due > new Date();
        },
    },

    actions: {
        async fetchUser() {
            this.loading = true;
            this.error = null;

            try {
                const [profile, packages, photos] = await Promise.all([
                    getMyProfile().catch(() => null),
                    getPackages().catch(() => null),
                    getPhoto().catch(() => []),
                ]);

                if (!profile) throw new Error("Профиль не найден");

                const normalizedPackage = packages
                    ? [{
                        subscription_package: packages.subscription_package,
                        superboom_due_date: packages.superboom_due_date,
                        superbooms: packages.superbooms,
                        superlikes: packages.superlikes,
                    }]
                    : [];

                const main = profile.images?.find((img: UserImageServer) => img.is_main) || null;

                this.user = {
                    ...profile,
                    photos,
                    information: {
                        ...profile.information,
                        packages: normalizedPackage,
                    },
                };
                this.mainImage = main;
            } catch (err: any) {
                this.error = err?.message || "Ошибка загрузки профиля";
                this.clearUser();
            } finally {
                this.loading = false;
            }
        },

        setUser(data: User) {
            this.user = data;
        },

        clearUser() {
            this.user = null;
            this.mainImage = null;
        },

        async activateSuperBoom() {
            this.loading = true;
            this.error = null;

            try {
                const res = await activateSuperBoomAPI();

                if (this.user?.information) {
                    if (typeof this.user.information.superbooms === "number" && this.user.information.superbooms > 0) {
                        this.user.information.superbooms -= 1;
                    }
                    if (this.user.information.packages?.[0]) {
                        this.user.information.packages[0].superboom_due_date =
                            res?.superboom_due_date || new Date(Date.now() + 30 * 60 * 1000).toISOString();
                    }
                }

                return res;
            } catch (err: any) {
                console.error("❌ Ошибка при активации супербума:", err);
                this.error = err?.message || "Ошибка активации супербума";
                throw err;
            } finally {
                this.loading = false;
            }
        },
        setBirthDateTime(birth_date: string, birth_time: string) {
            if (!this.user) return

            this.user.information.birth_date = birth_date
            this.user.information.birth_time = birth_time

            localStorage.setItem("user", JSON.stringify(this.user))
        },
    },

    persist: {
        key: 'user-store',
        storage: sessionStorage,
        pick: ['user', 'mainImage'],
    },
});
